import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Map as GoogleMap, useMap } from '@vis.gl/react-google-maps'
import type { MapEvent } from '@vis.gl/react-google-maps'
import type { BoxTruckMapDriver } from '../services/boxTruckLoadsApi'
import type { DriverClusterInspection, DriverClusterPoint, DriverFocusRequest, MapViewport, StateActivity, StateFocusRequest } from '../types/boxTruckMap'
import {
    BOX_TRUCK_MAP_DEFAULT_BOUNDS,
    BOX_TRUCK_MAP_DEFAULT_CENTER,
    BOX_TRUCK_MAP_DEFAULT_ZOOM,
    BOX_TRUCK_MAP_DRIVER_ZOOM,
    BOX_TRUCK_MAP_GEOCODE_TIMEOUT_MS,
    BOX_TRUCK_MAP_ID,
    BOX_TRUCK_MAP_LOADS_MIN_ZOOM,
    BOX_TRUCK_MAP_MAX_ZOOM,
    BOX_TRUCK_MAP_STATE_MARKER_MAX_ZOOM,
    BOX_TRUCK_MAP_STATE_ZOOM,
} from '../constants/boxTruckMap'
import { useDriverMapClusters } from '../hooks/useDriverMapClusters'
import { reverseLocality } from '@/components/geocode'
import { getUsStateCode, shouldResolveMapLoadState } from '../utils/boxTruckMapState'
import { BoxTruckDriverDetails } from './BoxTruckDriverDetails'
import { DriverClusterMarker, DriverMapMarker, StateActivityMarker } from './BoxTruckMapMarkers'

interface BoxTruckMapCanvasProps {
    states: StateActivity[]
    drivers: BoxTruckMapDriver[]
    selectedState: string | null
    selectedDriverId: string | null
    stateFocusRequest: StateFocusRequest | null
    focusRequest: DriverFocusRequest | null
    resetRequest: number
    onSelectState: (stateName: string | null) => void
    onSelectDriver: (driverId: string | null) => void
    onInspectCluster: (inspection: DriverClusterInspection | null) => void
    onFindLoads: (driver: BoxTruckMapDriver) => void
    onLoadStateChange: (stateCode: string) => void
    onLoadStateClear: () => void
}

interface BoxTruckMapContentProps extends Omit<BoxTruckMapCanvasProps, 'onLoadStateChange' | 'onLoadStateClear'> {
    viewport: MapViewport
}

/** Failure kinds already sent to Sentry this session: a lookup runs on every pan, so one outage is reported once. */
const reportedLookupFailures = new Set<string>()

const reportLookupFailure = (cause: unknown): void => {
    const status = (cause as { response?: { status?: number } })?.response?.status
    // A 429 is the server's per-user limit doing its job on a fast panner, not a fault.
    if (status === 429) return
    const kind = status ? `http_${status}` : 'network'
    if (reportedLookupFailures.has(kind)) return
    reportedLookupFailures.add(kind)
    Sentry.captureException(cause, { tags: { feature: 'box-truck-map', operation: 'resolve-viewport-state', kind } })
}

const sameViewport = (first: MapViewport, second: MapViewport): boolean =>
    first.zoom === second.zoom &&
    first.bounds.north === second.bounds.north &&
    first.bounds.south === second.bounds.south &&
    first.bounds.east === second.bounds.east &&
    first.bounds.west === second.bounds.west

const BoxTruckMapContent = ({
    states,
    drivers,
    selectedState,
    selectedDriverId,
    stateFocusRequest,
    focusRequest,
    resetRequest,
    viewport,
    onSelectState,
    onSelectDriver,
    onInspectCluster,
    onFindLoads,
}: BoxTruckMapContentProps) => {
    const map = useMap()
    const handledStateFocusRequest = useRef<number | null>(null)
    const handledFocusRequest = useRef<number | null>(null)
    const handledResetRequest = useRef(resetRequest)
    const { points, getClusterExpansionZoom, getClusterInspection } = useDriverMapClusters({ drivers, viewport })
    const selectedDriver = useMemo(() => drivers.find((driver) => driver.driverId === selectedDriverId) ?? null, [drivers, selectedDriverId])

    useEffect(() => {
        if (!map || !stateFocusRequest || handledStateFocusRequest.current === stateFocusRequest.requestId) return
        const state = states.find((candidate) => candidate.name === stateFocusRequest.stateName)
        if (!state) return
        handledStateFocusRequest.current = stateFocusRequest.requestId
        map.panTo(state.position)
        map.setZoom(BOX_TRUCK_MAP_STATE_ZOOM)
        onInspectCluster(null)
    }, [map, onInspectCluster, stateFocusRequest, states])

    useEffect(() => {
        if (!map || !focusRequest || handledFocusRequest.current === focusRequest.requestId) return
        const driver = drivers.find((candidate) => candidate.driverId === focusRequest.driverId)
        if (!driver) return
        handledFocusRequest.current = focusRequest.requestId
        map.panTo({ lat: driver.lat, lng: driver.lng })
        map.setZoom(BOX_TRUCK_MAP_DRIVER_ZOOM)
        onInspectCluster(null)
    }, [drivers, focusRequest, map, onInspectCluster])

    useEffect(() => {
        if (!map || handledResetRequest.current === resetRequest) return
        handledResetRequest.current = resetRequest
        map.setCenter(BOX_TRUCK_MAP_DEFAULT_CENTER)
        map.setZoom(BOX_TRUCK_MAP_DEFAULT_ZOOM)
    }, [map, resetRequest])

    const selectCluster = useCallback(
        (point: DriverClusterPoint) => {
            if (!map) return
            const expansionZoom = getClusterExpansionZoom(point.clusterId)
            onSelectDriver(null)
            onSelectState(null)
            map.panTo(point.position)

            if (expansionZoom > BOX_TRUCK_MAP_MAX_ZOOM) {
                map.setZoom(BOX_TRUCK_MAP_MAX_ZOOM)
                onInspectCluster(getClusterInspection(point.clusterId, point.count))
                return
            }

            onInspectCluster(null)
            map.setZoom(expansionZoom)
        },
        [getClusterExpansionZoom, getClusterInspection, map, onInspectCluster, onSelectDriver, onSelectState]
    )

    return (
        <>
            {viewport.zoom <= BOX_TRUCK_MAP_STATE_MARKER_MAX_ZOOM &&
                states
                    .filter((state) => state.loads !== null)
                    .map((state) => <StateActivityMarker key={state.name} state={state} selected={selectedState === state.name} onSelect={onSelectState} />)}
            {points.map((point) =>
                point.kind === 'cluster' ? (
                    <DriverClusterMarker key={`cluster-${point.clusterId}`} point={point} onSelect={selectCluster} />
                ) : (
                    <DriverMapMarker
                        key={point.driver.driverId}
                        driver={point.driver}
                        selected={selectedDriverId === point.driver.driverId}
                        onSelect={onSelectDriver}
                    />
                )
            )}
            {selectedDriver && <BoxTruckDriverDetails driver={selectedDriver} onClose={() => onSelectDriver(null)} onFindLoads={onFindLoads} />}
        </>
    )
}

interface ViewportStateLookup {
    promise: Promise<string | null>
    cancel: () => void
}

const createViewportStateLookup = (center: google.maps.LatLngLiteral): ViewportStateLookup => {
    let cancel: () => void = () => undefined
    const promise = new Promise<string | null>((resolve, reject) => {
        let settled = false
        const timeoutId = window.setTimeout(() => fail(new Error('Viewport state lookup timed out')), BOX_TRUCK_MAP_GEOCODE_TIMEOUT_MS)
        function finish(stateCode: string | null) {
            if (settled) return
            settled = true
            window.clearTimeout(timeoutId)
            resolve(stateCode)
        }
        function fail(cause: unknown) {
            if (settled) return
            settled = true
            window.clearTimeout(timeoutId)
            reject(cause)
        }
        cancel = () => finish(null)
        reverseLocality(center).then((locality) => finish(getUsStateCode(locality?.state)), fail)
    })
    return { promise, cancel: () => cancel() }
}

export const BoxTruckMapCanvas = ({ selectedState, onLoadStateChange, onLoadStateClear, ...contentProps }: BoxTruckMapCanvasProps) => {
    const [viewport, setViewport] = useState<MapViewport>({ bounds: BOX_TRUCK_MAP_DEFAULT_BOUNDS, zoom: BOX_TRUCK_MAP_DEFAULT_ZOOM })
    const stateLookupId = useRef(0)
    const cancelStateLookup = useRef<(() => void) | null>(null)

    useEffect(
        () => () => {
            stateLookupId.current += 1
            cancelStateLookup.current?.()
        },
        []
    )

    const handleIdle = useCallback(
        (event: MapEvent) => {
            const bounds = event.map.getBounds()?.toJSON()
            const center = event.map.getCenter()?.toJSON()
            const zoom = event.map.getZoom()
            if (!bounds || !center || zoom === undefined) return
            const nextViewport: MapViewport = { bounds, zoom }
            setViewport((current) => (sameViewport(current, nextViewport) ? current : nextViewport))

            cancelStateLookup.current?.()
            cancelStateLookup.current = null
            const requestId = ++stateLookupId.current
            if (zoom < BOX_TRUCK_MAP_LOADS_MIN_ZOOM) {
                onLoadStateClear()
                return
            }
            // State markers are hidden at deeper zooms, but an existing state selection still needs validation
            // after the viewport moves so the panel cannot keep showing a neighboring state's load count.
            if (!shouldResolveMapLoadState(zoom) && !selectedState) return

            const lookup = createViewportStateLookup(center)
            cancelStateLookup.current = lookup.cancel
            void lookup.promise
                .then((stateCode) => {
                    if (stateLookupId.current !== requestId) return
                    if (stateCode) onLoadStateChange(stateCode)
                    else onLoadStateClear()
                })
                .catch((cause: unknown) => {
                    if (stateLookupId.current !== requestId) return
                    reportLookupFailure(cause)
                    // Reverse geocoding is an enhancement for manual panning. Keep the last known/explicit state
                    // when the lookup is unavailable; an explicit state click has already requested its count directly.
                })
                .finally(() => {
                    if (cancelStateLookup.current === lookup.cancel) cancelStateLookup.current = null
                })
        },
        [onLoadStateChange, onLoadStateClear, selectedState]
    )

    return (
        <GoogleMap
            defaultCenter={BOX_TRUCK_MAP_DEFAULT_CENTER}
            defaultZoom={BOX_TRUCK_MAP_DEFAULT_ZOOM}
            maxZoom={BOX_TRUCK_MAP_MAX_ZOOM}
            gestureHandling="greedy"
            disableDefaultUI={false}
            mapId={BOX_TRUCK_MAP_ID}
            fullscreenControl={false}
            zoomControl
            mapTypeControl={false}
            scaleControl
            reuseMaps
            onIdle={handleIdle}
        >
            <BoxTruckMapContent {...contentProps} selectedState={selectedState} viewport={viewport} />
        </GoogleMap>
    )
}
